import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BoxScore from '../components/BoxScore';
import './LeagueDetailPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { SUPABASE_ANON_KEY as supabaseKey, FUNCTIONS_URL } from '../services/supabaseConfig';
import { gameDayToShortDate, gameDayToDate, formatSimDate, formatSimDateShort } from '../utils/dateUtils';
import { numberToGrade, getOverallColor } from '../utils/grades';
import { isCommissioner } from '../config/gameConfig';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';

const RARITY_COLORS: Record<string, string> = {
  common: '#60a5fa',
  uncommon: '#4ade80',
  rare: '#818cf8',
  epic: '#c084fc',
};

interface LeagueFromDB {
  id: string;
  name: string;
  tier: number;
  status: string;
  created_at: string;
  entry_fee?: number;
  season_prizes?: { position: number; amount: number }[];
  is_engaged?: boolean;
  join_window_end?: string;
  setup_window_end?: string;
  league_type?: string;
  season_year?: number;
  current_day?: number;
  champion_team_id?: string;
  champion_name?: string;
}

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  division: string;
  conference: string;
  wins: number;
  losses: number;
  owner_id?: string;
}

interface StandingFromDB {
  team_id: string;
  wins: number;
  losses: number;
  runs_scored: number;
  runs_allowed: number;
  division: string;
  conference: string;
}

interface GameFromDB {
  id: string;
  game_id?: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  winner?: string;
  game_day: number;
  status: string;
  is_playoff?: boolean;
  series_id?: string;
}

interface LeagueDetailPageProps {
  leagueId?: string;
  onNavigate?: (page: string) => void;
  userData?: any;
}

const LeagueDetailPage: React.FC<LeagueDetailPageProps> = ({ onNavigate, userData }) => {
  const params = useParams();
  const navigate = useNavigate();
  const leagueId = params.leagueId;
  const currentUserId = userData?.id || localStorage.getItem('userId');
  console.log("[LeagueDetailPage] leagueId:", leagueId);
  const [league, setLeague] = useState<LeagueFromDB | null>(null);
  const [teams, setTeams] = useState<TeamFromDB[]>([]);
  const [standings, setStandings] = useState<Map<string, StandingFromDB>>(new Map());
  const [completedGames, setCompletedGames] = useState<GameFromDB[]>([]);
  const [loading, setLoading] = useState(true);
  const [simState, setSimState] = useState<any>(null);
  const [activeTab, setActiveTabRaw] = useState<'standings' | 'stats' | 'games' | 'playoffs' | 'free-agents' | 'draft' | 'draft-sheet'>(() => {
    const hash = window.location.hash.replace('#', '');
    const valid = ['standings', 'stats', 'games', 'playoffs', 'free-agents', 'draft', 'draft-sheet'];
    return (valid.includes(hash) ? hash : 'standings') as any;
  });

  const setActiveTab = (tab: typeof activeTab) => {
    setActiveTabRaw(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };
  const [paidTeams, setPaidTeams] = useState<Set<string>>(new Set());
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [simSpeed, setSimSpeed] = useState<number>(15);
  const [playerStats, setPlayerStats] = useState<any[]>([]);
  const [playoffPlayerStats, setPlayoffPlayerStats] = useState<any[]>([]);
  const [teamOwners, setTeamOwners] = useState<Map<string, string>>(new Map());
  const [claimingTeamId, setClaimingTeamId] = useState<string | null>(null);
  const [claimName, setClaimName] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [startingPlayoffs, setStartingPlayoffs] = useState(false);
  const [playoffMsg, setPlayoffMsg] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [leagueId]);

  async function loadData() {
    setLoading(true);
    
    if (isSupabaseConfigured() && supabase) {
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('*')
        .eq('id', leagueId || 'global')
        .single();
      
      if (leagueData) {
        setLeague(leagueData as LeagueFromDB);
        setSimSpeed((leagueData as any).sim_speed || 15);
      }
      
      const { data: teamData } = await supabase
        .from('teams')
        .select('*')
        .eq('league_id', leagueId)
        .order('name', { ascending: true });
      
      if (teamData) setTeams(teamData as TeamFromDB[]);
      
      // Load owner names for teams
      if (teamData && teamData.length > 0) {
        const ownerIds = (teamData as TeamFromDB[])
          .filter(t => t.owner_id && t.owner_id !== t.id)
          .map(t => t.owner_id);
        
        if (ownerIds.length > 0) {
          const { data: ownerData } = await supabase
            .from('users')
            .select('id, username')
            .in('id', ownerIds);
          
          if (ownerData) {
            const ownerMap = new Map<string, string>();
            ownerData.forEach((o: any) => ownerMap.set(o.id, o.username));
            setTeamOwners(ownerMap);
          }
        }
      }
      
      const currentSeasonYear = (leagueData as any)?.season_year || new Date().getFullYear();
      const { data: standingData } = await supabase
        .from('league_standings')
        .select('*')
        .eq('league_id', leagueId)
        .eq('season_year', currentSeasonYear)
        .order('wins', { ascending: false });
      
      if (standingData) {
        const standingMap = new Map<string, StandingFromDB>();
        (standingData as StandingFromDB[]).forEach(s => {
          standingMap.set(s.team_id, s);
        });
        setStandings(standingMap);
      }
      
      const { data: gameData } = await supabase
        .from('league_schedule')
        .select('id,game_id,home_team_id,away_team_id,home_score,away_score,winner,game_day,status,is_playoff,series_id')
        .eq('league_id', leagueId)
        .eq('status', 'completed')
        .order('game_day', { ascending: false })
        .order('game_number', { ascending: false });
      
      if (gameData) setCompletedGames(gameData as GameFromDB[]);
      
      // Load available seasons
      const { data: seasonData } = await supabase
        .from('player_season_stats')
        .select('season_year')
        .eq('league_id', leagueId)
        .order('season_year', { ascending: false });
      
      // Load player stats - fetch current season + all other seasons for the dropdown
      // Current season (default view)
      const { data: currentSeasonStats } = await supabase
        .from('player_season_stats')
        .select('*')
        .eq('league_id', leagueId)
        .eq('season_year', currentSeasonYear)
        .gt('games', 0);
      // Older seasons (for season selector) — paginated
      const { data: olderSeasonStats } = await supabase
        .from('player_season_stats')
        .select('*')
        .eq('league_id', leagueId)
        .neq('season_year', currentSeasonYear)
        .gt('games', 0)
        .limit(5000);
      const statsData = [...(currentSeasonStats || []), ...(olderSeasonStats || [])];
      
      if (statsData && statsData.length > 0) {
        // Fetch player names
        const playerIds = Array.from(new Set(statsData.map((s: any) => s.player_id)));
        // Fetch player names in batches (Supabase .in() has limits)
        const playerData: any[] = [];
        for (let i = 0; i < playerIds.length; i += 200) {
          const batch = playerIds.slice(i, i + 200);
          const { data } = await supabase
            .from('players')
            .select('id, first_name, last_name, position')
            .in('id', batch);
          if (data) playerData.push(...data);
        }
        
        const playerMap = new Map((playerData || []).map((p: any) => [p.id, p]));
        const enriched = statsData.map((s: any) => {
          const player = playerMap.get(s.player_id);
          return { ...s, playerName: player ? `${player.first_name} ${player.last_name}` : 'Unknown', position: player?.position || '-',  };
        });
        setPlayerStats(enriched);
      }

      // Load playoff game stats from player_game_stats (aggregated per player/season)
      const { data: playoffGameIds } = await supabase
        .from('league_schedule')
        .select('game_id')
        .eq('league_id', leagueId)
        .eq('is_playoff', true)
        .eq('status', 'completed');
      
      if (playoffGameIds && playoffGameIds.length > 0) {
        const poGameIds = playoffGameIds.map((g: any) => g.game_id);
        // Fetch in batches (Supabase .in() has limits)
        const allPoStats: any[] = [];
        for (let i = 0; i < poGameIds.length; i += 50) {
          const batch = poGameIds.slice(i, i + 50);
          const { data: poStats } = await supabase
            .from('player_game_stats')
            .select('*')
            .eq('league_id', leagueId)
            .in('game_id', batch);
          if (poStats) allPoStats.push(...poStats);
        }
        
        if (allPoStats.length > 0) {
          // Aggregate by player_id + season_year + is_pitcher
          const aggMap = new Map<string, any>();
          for (const s of allPoStats) {
            const key = `${s.player_id}|${s.season_year}|${s.is_pitcher}`;
            if (!aggMap.has(key)) {
              aggMap.set(key, {
                player_id: s.player_id,
                team_id: s.team_id,
                season_year: s.season_year,
                is_pitcher: s.is_pitcher,
                games: 0,
                plate_appearances: 0, at_bats: 0, runs: 0, hits: 0,
                doubles: 0, triples: 0, home_runs: 0, rbi: 0,
                walks: 0, strikeouts: 0, stolen_bases: 0,
                innings_pitched: 0, hits_allowed: 0, runs_allowed: 0, earned_runs: 0,
                walks_allowed: 0, strikeouts_pitched: 0, home_runs_allowed: 0,
                wins: 0, losses: 0, saves: 0, starts: 0,
                hit_by_pitch: 0, sacrifice_flies: 0, sacrifice_hits: 0, grounded_into_dp: 0,
                pitches_thrown: 0, caught_stealing: 0, batters_faced: 0,
                holds: 0, blown_saves: 0, quality_starts: 0, complete_games: 0, shutouts: 0,
              });
            }
            const agg = aggMap.get(key)!;
            agg.games++;
            agg.plate_appearances += s.plate_appearances || 0;
            agg.at_bats += s.at_bats || 0;
            agg.runs += s.runs || 0;
            agg.hits += s.hits || 0;
            agg.doubles += s.doubles || 0;
            agg.triples += s.triples || 0;
            agg.home_runs += s.home_runs || 0;
            agg.rbi += s.rbi || 0;
            agg.walks += s.walks || 0;
            agg.strikeouts += s.strikeouts || 0;
            agg.stolen_bases += s.stolen_bases || 0;
            agg.innings_pitched += parseFloat(s.innings_pitched || 0);
            agg.hits_allowed += s.hits_allowed || 0;
            agg.runs_allowed += s.runs_allowed || 0;
            agg.earned_runs += s.earned_runs || 0;
            agg.walks_allowed += s.walks_allowed || 0;
            agg.strikeouts_pitched += s.strikeouts_pitched || 0;
            agg.home_runs_allowed += s.home_runs_allowed || 0;
            agg.wins += s.win ? 1 : 0;
            agg.losses += s.loss ? 1 : 0;
            agg.saves += s.save ? 1 : 0;
            agg.starts += s.starts || 0;
            agg.hit_by_pitch += s.hit_by_pitch || 0;
            agg.sacrifice_flies += s.sacrifice_flies || 0;
            agg.sacrifice_hits += s.sacrifice_hits || 0;
            agg.grounded_into_dp += s.grounded_into_dp || 0;
            agg.pitches_thrown += s.pitches_thrown || 0;
            agg.caught_stealing += s.caught_stealing || 0;
            agg.batters_faced += s.batters_faced || 0;
          }
          
          // Enrich with player names
          const poPlayerIds = Array.from(new Set(allPoStats.map((s: any) => s.player_id)));
          const poPlayerData: any[] = [];
          for (let i = 0; i < poPlayerIds.length; i += 200) {
            const batch = poPlayerIds.slice(i, i + 200);
            const { data } = await supabase
              .from('players')
              .select('id, first_name, last_name, position')
              .in('id', batch);
            if (data) poPlayerData.push(...data);
          }
          const poPlayerMap = new Map((poPlayerData || []).map((p: any) => [p.id, p]));
          
          const enrichedPo = Array.from(aggMap.values()).map((s: any) => {
            const player = poPlayerMap.get(s.player_id);
            return {
              ...s,
              playerName: player ? `${player.first_name} ${player.last_name}` : 'Unknown',
              position: player?.position || '-',
            };
          });
          setPlayoffPlayerStats(enrichedPo);
        }
      }
      
      // Get sim state from the league itself (not simulation_state, which is global/legacy)
      if (leagueData) {
        setSimState({
          current_day: leagueData.current_day,
          season_year: leagueData.season_year,
          status: leagueData.status,
        } as any);
      }

      const { data: payments } = await supabase
        .from('league_entry_payments')
        .select('team_id')
        .eq('league_id', leagueId);
      if (payments) {
        setPaidTeams(new Set(payments.map(p => p.team_id)));
      }
    }
    
    setLoading(false);
  }

  const getTeam = (teamId: string): TeamFromDB | undefined => {
    return teams.find(t => t.id === teamId);
  };

  // Compute derived standings stats from completed games
  const computeTeamStats = (teamId: string) => {
    const teamGames = completedGames
      .filter(g => g.home_team_id === teamId || g.away_team_id === teamId)
      .sort((a, b) => (a.game_day || 0) - (b.game_day || 0));

    let homeW = 0, homeL = 0, awayW = 0, awayL = 0;
    let divW = 0, divL = 0;
    const team = getTeam(teamId);

    teamGames.forEach(g => {
      const isHome = g.home_team_id === teamId;
      const myScore = isHome ? g.home_score : g.away_score;
      const oppScore = isHome ? g.away_score : g.home_score;
      const won = (myScore ?? 0) > (oppScore ?? 0);
      if (isHome) { won ? homeW++ : homeL++; } else { won ? awayW++ : awayL++; }

      // Div record: opponent in same division
      const oppId = isHome ? g.away_team_id : g.home_team_id;
      const opp = getTeam(oppId);
      if (team && opp && team.division === opp.division && team.conference === opp.conference) {
        won ? divW++ : divL++;
      }
    });

    // Last 10
    const last10 = teamGames.slice(-10);
    const l10W = last10.filter(g => {
      const isHome = g.home_team_id === teamId;
      const myScore = isHome ? g.home_score : g.away_score;
      const oppScore = isHome ? g.away_score : g.home_score;
      return (myScore ?? 0) > (oppScore ?? 0);
    }).length;
    const l10L = last10.length - l10W;

    // Streak
    let streak = 0;
    let streakType = '';
    for (let i = teamGames.length - 1; i >= 0; i--) {
      const g = teamGames[i];
      const isHome = g.home_team_id === teamId;
      const myScore = isHome ? g.home_score : g.away_score;
      const oppScore = isHome ? g.away_score : g.home_score;
      const won = (myScore ?? 0) > (oppScore ?? 0);
      if (streak === 0) { streakType = won ? 'W' : 'L'; streak = 1; }
      else if ((won && streakType === 'W') || (!won && streakType === 'L')) streak++;
      else break;
    }

    return {
      homeRecord: `${homeW}-${homeL}`,
      awayRecord: `${awayW}-${awayL}`,
      divRecord: `${divW}-${divL}`,
      l10: `${l10W}-${l10L}`,
      streak: streak > 0 ? `${streakType}${streak}` : '-',
    };
  };

  if (loading) {
    return <div className="league-detail-page"><div className="loading-container">Loading...</div></div>;
  }

  // Compute league display name with sim date
  const getLeagueDisplayName = (): string => {
    if (!league?.name) return 'League';
    // Strip any year from the name (e.g., "Major League Baseball 2026" → "Major League Baseball")
    const baseName = league.name.replace(/\s*\d{4}\s*$/, '').trim();
    
    // Use league's own data, not simState (which may be null/stale)
    const displayDay = league.current_day || simState?.current_day;
    const displayYear = league.season_year || simState?.season_year;
    
    if (!displayDay || !displayYear) return baseName;
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Use sim_date if available — it's the exact date stored by the engine
    const simDateVal = (league as any)?.sim_date;
    const dateStr = simDateVal
      ? formatSimDateShort(simDateVal)
      : (() => {
          const st = (league?.status || 'active') as 'active' | 'playoffs' | 'off_season';
          const d = gameDayToDate(displayDay, displayYear, st);
          return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${displayYear}`;
        })();

    if (league?.status === 'playoffs') return `${baseName} — Playoffs Day ${displayDay} (${dateStr})`;
    if (league?.status === 'off_season') return `${baseName} — Off-Season (${dateStr})`;
    return `${baseName} — ${dateStr}`;
  };

  // Check if this is a quick league in lobby mode (still filling)
  const isLobbyMode = (): boolean => {
    if (!league?.league_type) return false;
    const quickTypes: readonly string[] = [LEAGUE_TYPE.QUICK];
    if (!quickTypes.includes(league.league_type)) return false;
    if (league.status === 'completed' || league.status === 'off_season') return false;
    // Lobby during: waiting (standby), active with open join window, drafting, free_agency
    if (league.status === 'waiting' || league.status === 'drafting' || league.status === 'free_agency') return true;
    if (league.status === 'active' && league.join_window_end && new Date(league.join_window_end) > new Date()) return true;
    // Active league with expired join window = sim is running, show normal view
    return false;
  };

  // Get max teams for league type
  const getMaxTeams = (): number => {
    const configs: Record<string, number> = {
      'quick': 12,
    };
    return configs[league?.league_type || ''] || 12;
  };

  // Get entry fee for quick league
  const getEntryFee = (): number => {
    const fees: Record<string, number> = {
      'quick_12_low': 2500, 'quick_12_high': 10000, 'quick_24_low': 2500, 'quick_24_high': 10000,
      'fast_12': 2500,
    };
    return fees[league?.league_type || ''] || 0;
  };

  // Claim a team in lobby
  async function doClaim(teamId: string) {
    if (!currentUserId || claiming) return;
    const fee = getEntryFee();
    setClaiming(true);
    try {
      const r = await fetch(`${FUNCTIONS_URL}/team-factory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
        body: JSON.stringify({ userId: currentUserId, claimTeamId: teamId, leagueId, renameTeam: claimName.trim() || undefined }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setClaimingTeamId(null);
      setClaimName('');
      // Go to league lobby page after claim — shows draft sheet tab when in drafting mode
      if (data.teamId) {
        onNavigate?.(`/league/${leagueId}`);
      } else {
        loadData(); // Refresh lobby
      }
    } catch (e: any) {
      alert('Claim failed: ' + e.message);
    }
    setClaiming(false);
  }

  // Start playoffs (commissioner manual trigger)
  async function startPlayoffs() {
    if (!league || startingPlayoffs) return;
    const confirm = window.confirm(
      `Start playoffs for ${league.name} (Season ${league.season_year})?\n\nThis will generate Division Series brackets and schedule all playoff games.`
    );
    if (!confirm) return;
    setStartingPlayoffs(true);
    setPlayoffMsg(null);
    try {
      const r = await fetch(`${FUNCTIONS_URL}/start-playoffs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-secret': 'simforge-admin-2024',
        },
        body: JSON.stringify({ league_id: leagueId }),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        setPlayoffMsg(`❌ ${data.error || 'Failed to start playoffs'}`);
      } else {
        const matchupStr = (data.matchups || [])
          .map((m: any) => `• ${m.division}: ${m.home} vs ${m.away}`)
          .join('\n');
        setPlayoffMsg(`✅ Playoffs started! ${data.games_scheduled} games scheduled.\n${matchupStr}`);
        setLeague({ ...league, status: 'playoffs', current_day: 184 });
        await loadData();
      }
    } catch (e: any) {
      setPlayoffMsg(`❌ Error: ${e.message}`);
    }
    setStartingPlayoffs(false);
  }

  // Format countdown
  const formatCountdown = (endDate: string): string => {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return 'Starting...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${mins}m remaining`;
    return `${mins} minutes remaining`;
  };

  return (
    <>
      <div className="league-detail-page">
        <div className="page-header">
          <h1>{getLeagueDisplayName()}</h1>
          <div style={{ marginTop: '10px' }}>
            {userData?.email && isCommissioner(userData.email) && (
              <>
                {league?.is_engaged === false ? (
                  <button 
                    className="save-btn" 
                    style={{ background: '#22c55e' }}
                    onClick={async () => {
                    if (!isSupabaseConfigured() || !supabase || !league) return;
                    await supabase.from('leagues').update({ is_engaged: true }).eq('id', leagueId);
                    setLeague({ ...league, is_engaged: true });
                  }}
                >
                  ▶️ Resume (Engage Clutch)
                </button>
              ) : (
                <button 
                  className="save-btn" 
                  style={{ background: '#ef4444' }}
                  onClick={async () => {
                    if (!isSupabaseConfigured() || !supabase || !league) return;
                    await supabase.from('leagues').update({ is_engaged: false }).eq('id', leagueId);
                    setLeague({ ...league, is_engaged: false });
                  }}
                >
                  ⏸️ Pause (Disengage Clutch)
                </button>
              )}
              {league?.is_engaged === false && (
                <span style={{ marginLeft: '10px', color: '#ef4444' }}>⏸️ Paused</span>
              )}
              {/* Start Playoffs button — shown when regular season is complete */}
              {league?.status === 'playoffs' && !league?.champion_name && (
                <button
                  className="save-btn"
                  style={{ background: '#f59e0b', marginLeft: '10px' }}
                  onClick={startPlayoffs}
                  disabled={startingPlayoffs}
                  title="Generate playoff bracket and schedule games"
                >
                  {startingPlayoffs ? '⏳ Generating...' : '🏆 Start Playoffs'}
                </button>
              )}
              {playoffMsg && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: playoffMsg.startsWith('✅') ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  color: playoffMsg.startsWith('✅') ? '#4ade80' : '#f87171',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-line',
                }}>
                  {playoffMsg}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Champion Banner */}
      {(league?.status === 'off_season' || league?.status === 'playoffs') && league?.champion_name && (
        <div className="champion-banner">
          <div className="champion-trophy">🏆</div>
          <div className="champion-text">
            <div className="champion-label">WORLD SERIES CHAMPION</div>
            <div className="champion-name">{league.champion_name}</div>
            <div className="champion-season">Season {league.season_year || '?'}</div>
          </div>
        </div>
      )}

      {/* League Score Ticker */}
      {completedGames.length > 0 && (
        <div className="league-ticker">
          <div className="league-ticker-scroll">
            {[...completedGames.slice(0, 20), ...completedGames.slice(0, 20)].map((game, i) => {
              const awayTeam = getTeam(game.away_team_id);
              const homeTeam = getTeam(game.home_team_id);
              const awayWon = game.away_score > game.home_score;
              const homeWon = game.home_score > game.away_score;
              return (
                <span 
                  key={`${game.id}-${i}`} 
                  className="league-ticker-game"
                  onClick={() => setSelectedGameId(game.game_id ?? game.id)}
                >
                  <span className={awayWon ? 'team-abbr winner' : 'team-abbr'}>{awayTeam?.abbreviation || '???'}</span>
                  <span className="score">{game.away_score}</span>
                  <span className="vs">@</span>
                  <span className="score">{game.home_score}</span>
                  <span className={homeWon ? 'team-abbr winner' : 'team-abbr'}>{homeTeam?.abbreviation || '???'}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* LOBBY MODE - Show waiting area for quick leagues */}
      {isLobbyMode() ? (
        <div className="lobby-container">
          <div className="lobby-header">
            <h2>🏟️ {league?.status === 'drafting' ? 'Draft in Progress' : league?.status === 'free_agency' ? 'Free Agency Open' : 'Lobby — Waiting for Players'}</h2>
            <div className="lobby-countdown">
              {league?.join_window_end && league.status === 'active' && formatCountdown(league.join_window_end)}
            </div>
          </div>
          
          <div className="lobby-progress">
            <div className="lobby-progress-bar">
              <div 
                className="lobby-progress-fill" 
                style={{ width: `${(teams.filter(t => t.owner_id).length / getMaxTeams()) * 100}%` }}
              />
            </div>
            <span className="lobby-progress-text">
              {teams.filter(t => t.owner_id).length} / {getMaxTeams()} teams owned (min 8 to start)
            </span>
          </div>

          {/* Tabs for lobby mode */}
          <div className="tabs" style={{marginBottom: 16}}>
            <button className={`tab ${activeTab === 'standings' ? 'active' : ''}`} onClick={() => setActiveTab('standings')}>🏟️ Teams</button>
            {league?.status === 'free_agency' && (
              <button className={`tab ${activeTab === 'free-agents' ? 'active' : ''}`} onClick={() => setActiveTab('free-agents')}>🤝 Free Agents</button>
            )}
            {league?.status === 'drafting' && currentUserId && teams.some(t => t.owner_id === currentUserId) && (
              <button 
                className={`tab ${activeTab === 'draft-sheet' ? 'active' : ''}`} 
                onClick={() => onNavigate?.(`/league/${leagueId}/draft-sheet`)}
                style={{ background: '#8b5cf6', color: '#fff', fontWeight: 700 }}
              >📋 Draft Sheet — Set Your Priorities!</button>
            )}
          </div>

          {activeTab === 'standings' && (
          <div className="lobby-teams">
            {/* Group by conference */}
            {Array.from(new Set(teams.map(t => t.conference || 'League'))).map(conf => {
              const confTeams = teams.filter(t => (t.conference || 'League') === conf);
              return (
                <div key={conf} style={{marginBottom: 16}}>
                  <h3 style={{color:'#9ca3af', fontSize:'0.85rem', marginBottom: 8}}>{conf} Conference</h3>
                  <table className="lobby-table">
                    <thead>
                      <tr><th>#</th><th>Team</th><th>Owner</th><th></th></tr>
                    </thead>
                    <tbody>
                      {confTeams.map((team, idx) => {
                        const isOwned = !!team.owner_id;
                        const isMine = team.owner_id === currentUserId;
                        return (
                          <tr key={team.id} className="lobby-team-row" style={{background: isMine ? 'rgba(74,222,128,0.08)' : isOwned ? 'transparent' : 'rgba(59,130,246,0.05)'}}>
                            <td className="lobby-rank">{idx + 1}</td>
                            <td className="lobby-team-name" style={{cursor:'pointer'}} onClick={() => {
                              if (isMine && league?.status === 'drafting') {
                                onNavigate?.(`/league/${leagueId}/draft-sheet`);
                              } else {
                                onNavigate?.(`/team/${team.id}`);
                              }
                            }}>
                              {team.name.startsWith(team.city) ? team.name : `${team.city} ${team.name}`}
                              {isMine && ' ⭐'}
                            </td>
                            <td className="lobby-owner">
                              {isOwned ? (isMine ? 'You' : (teamOwners.get(team.owner_id!) || 'Taken')) : '—'}
                            </td>
                            <td>
                              {!isOwned && currentUserId && league?.status === 'active' && (
                                <button onClick={() => { setClaimingTeamId(team.id); setClaimName(''); }}
                                  style={{padding:'4px 12px', borderRadius:6, border:'none', background:'#3b82f6', color:'#fff', cursor:'pointer', fontSize:'0.8rem', fontWeight:600}}>
                                  Buy In 🎟️
                                </button>
                              )}
                              {isMine && league?.status === 'drafting' && (
                                <button onClick={() => onNavigate?.(`/league/${leagueId}/draft-sheet`)}
                                  style={{padding:'4px 12px', borderRadius:6, border:'none', background:'#8b5cf6', color:'#fff', cursor:'pointer', fontSize:'0.8rem', fontWeight:600}}>
                                  📋 Draft Sheet
                                </button>
                              )}
                              {isMine && league?.status === 'free_agency' && (
                                <button onClick={() => onNavigate?.(`/league/${leagueId}/free-agency`)}
                                  style={{padding:'4px 12px', borderRadius:6, border:'none', background:'#22c55e', color:'#fff', cursor:'pointer', fontSize:'0.8rem', fontWeight:600}}>
                                  🤝 Free Agency
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
            <div className="lobby-info">
              <p>Entry Fee: <strong>{getEntryFee().toLocaleString()} $BALLS</strong> · Need 8+ owners to start draft</p>
              <p>If fewer than 8 in 24hrs → league dissolved, full refund</p>
            </div>
          </div>
          )}

          {/* Claim Modal */}
          {claimingTeamId && (() => {
            const team = teams.find(t => t.id === claimingTeamId);
            const fee = getEntryFee();
            if (!team) return null;
            return (
              <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}
                onClick={() => {setClaimingTeamId(null);setClaimName('');}}>
                <div style={{background:'#0d1117',borderRadius:16,maxWidth:400,width:'100%',border:'1px solid rgba(255,255,255,0.1)',padding:24}} onClick={e => e.stopPropagation()}>
                  <h3 style={{color:'#fff',margin:'0 0 16px',textAlign:'center'}}>🎟️ Claim This Team</h3>
                  <p style={{color:'#9ca3af',textAlign:'center',marginBottom:16}}>{team.name} · {fee.toLocaleString()} $BALLS</p>
                  <div style={{marginBottom:12}}>
                    <label style={{color:'#aaa',fontSize:'0.85rem',display:'block',marginBottom:6}}>Rename Team (optional)</label>
                    <input type="text" value={claimName} onChange={e => setClaimName(e.target.value)} placeholder={team.name} maxLength={30}
                      style={{width:'100%',padding:'12px 16px',borderRadius:8,border:'1px solid #444',background:'#1a1a2e',color:'#fff',fontSize:'1rem',boxSizing:'border-box'}} />
                  </div>
                  <div style={{background:'#1a1a2e',borderRadius:8,padding:12,marginBottom:16,fontSize:'0.8rem',color:'#888'}}>
                    <div>• 15 scrub players auto-assigned</div>
                    <div>• 15 min to set draft priority after 8+ owners</div>
                    <div>• 10 players via snake draft</div>
                    <div>• $1M FA budget for free agency</div>
                  </div>
                  <div style={{display:'flex',gap:10}}>
                    <button onClick={() => {setClaimingTeamId(null);setClaimName('');}}
                      style={{flex:1,padding:12,borderRadius:8,border:'1px solid #444',background:'transparent',color:'#888',cursor:'pointer',fontSize:16}}>Cancel</button>
                    <button onClick={() => doClaim(claimingTeamId)} disabled={claiming}
                      style={{flex:2,padding:12,borderRadius:8,border:'none',background:claiming?'#444':'#3b82f6',color:'#fff',cursor:claiming?'default':'pointer',fontSize:16,fontWeight:600}}>
                      {claiming ? 'Claiming...' : `Claim — ${fee.toLocaleString()} $BALLS`}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <>
          <div className="tabs">
            <button className={`tab ${activeTab === 'standings' ? 'active' : ''}`} onClick={() => setActiveTab('standings')}>📊 Standings</button>
            <button className={`tab ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>🏆 Leaders</button>
            <button className={`tab ${activeTab === 'games' ? 'active' : ''}`} onClick={() => setActiveTab('games')}>📅 Schedule</button>
            <button className={`tab ${activeTab === 'playoffs' ? 'active' : ''}`} onClick={() => setActiveTab('playoffs')}>⚾ Playoffs</button>
            <button className={`tab ${activeTab === 'draft' ? 'active' : ''}`} onClick={() => setActiveTab('draft')}>📋 Draft</button>
          </div>

      {activeTab === 'standings' && (
        <div className="tab-content">
          <h3>League Standings</h3>
          {standings.size === 0 ? (
            <p>No standings data yet. Advance to Day 1 to generate standings.</p>
          ) : (
            (() => {
              const isQuick = league?.league_type === LEAGUE_TYPE.QUICK || QL_ARRAY.includes(league?.league_type as any);
              const confOrder = ['American', 'National', 'AL', 'NL'];
              const grouped: Record<string, Array<{teamId: string; s: StandingFromDB; team: TeamFromDB | undefined}>> = {};

              Array.from(standings).forEach(([teamId, s]) => {
                const team = getTeam(teamId);
                const conf = team?.conference || s.conference || 'Unknown';
                if (!grouped[conf]) grouped[conf] = [];
                grouped[conf].push({ teamId, s, team });
              });

              const renderTableRows = (sorted: Array<{teamId: string; s: StandingFromDB; team: TeamFromDB | undefined}>, leader: {s: StandingFromDB}) => sorted.map(({ teamId, s, team }, idx) => {
                const pct = s.wins + s.losses === 0 ? '.000' : (s.wins / (s.wins + s.losses)).toFixed(3).replace('0.', '.');
                const gb = idx === 0 ? '-' : (() => {
                  const g = ((leader.s.wins - s.wins) + (s.losses - leader.s.losses)) / 2;
                  return g <= 0 ? '-' : g % 1 === 0 ? String(g) : g.toFixed(1);
                })();
                const diff = s.runs_scored - s.runs_allowed;
                const teamName = team ? team.name : teamId;
                const derived = computeTeamStats(teamId);
                const streakClass = derived.streak.startsWith('W') ? 'streak-w' : derived.streak.startsWith('L') ? 'streak-l' : '';
                return (
                  <tr key={teamId} className={idx === 0 ? 'leader-row' : ''}>
                    <td className="rank-cell">{idx + 1}</td>
                    <td className="team-name-cell">
                      <span className="team-link" onClick={() => navigate(isQuick ? `/ql-team/${leagueId}/${teamId}` : `/team/${teamId}`)}
                        style={{cursor:'pointer',color:'#60a5fa'}}>{teamName}</span>
                    </td>
                    <td className="w-cell">{s.wins}</td><td className="l-cell">{s.losses}</td>
                    <td className="pct-cell">{pct}</td><td className="gb-cell">{gb}</td>
                    <td className="l10-cell">{derived.l10}</td>
                    <td className={`strk-cell ${streakClass}`}>{derived.streak}</td>
                    <td className="div-cell">{derived.divRecord}</td>
                    <td className="home-cell">{derived.homeRecord}</td>
                    <td className="away-cell">{derived.awayRecord}</td>
                    <td className="rs-cell">{s.runs_scored}</td>
                    <td className="ra-cell">{s.runs_allowed}</td>
                    <td className={`diff-cell ${diff>0?'pos':diff<0?'neg':''}`}>{diff>0?'+':''}{diff}</td>
                  </tr>
                );
              });

              return confOrder.map(conf => {
                const confTeams = grouped[conf];
                if (!confTeams?.length) return null;

                // For traditional leagues (not quick): group by division within each conference
                const divisions = isQuick ? null : Array.from(new Set(confTeams.map(t => t.team?.division || 'Unknown').filter(d => d !== 'Unknown')));
                const hasDivisions = divisions && divisions.length > 1;

                // If divisions exist, render division subgroups with separate GB leaders
                if (hasDivisions) {
                  return (
                    <div key={conf} className="conf-standings-block" style={{width:'100%'}}>
                      <h4 className="conf-standings-header">
                        🏟️ {conf === 'AL' ? 'American League' : conf === 'NL' ? 'National League' : conf}
                      </h4>
                      <table className="standings-table standings-full">
                        <thead><tr>
                          <th>#</th><th className="team-th">Team</th>
                          <th>W</th><th>L</th><th>Pct</th><th>GB</th>
                          <th>L10</th><th>Strk</th><th>Div</th>
                          <th>Home</th><th>Away</th><th>RS</th><th>RA</th><th>Diff</th>
                        </tr></thead>
                        <tbody>
                          {divisions.sort().map(div => {
                            const divTeams = confTeams.filter(t => (t.team?.division || 'Unknown') === div);
                            const divSorted = [...divTeams].sort((a, b) => {
                              const pa = a.s.wins + a.s.losses === 0 ? 0 : a.s.wins / (a.s.wins + a.s.losses);
                              const pb = b.s.wins + b.s.losses === 0 ? 0 : b.s.wins / (b.s.wins + b.s.losses);
                              if (pb !== pa) return pb - pa;
                              return (b.s.runs_scored - b.s.runs_allowed) - (a.s.runs_scored - a.s.runs_allowed);
                            });
                            const divLeader = divSorted[0];
                            return (
                              <React.Fragment key={div}>
                                <tr className="division-header-row">
                                  <td colSpan={14} style={{
                                    padding: '8px 14px',
                                    background: 'rgba(232, 184, 42, 0.1)',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    color: '#e8b82a',
                                    borderBottom: '1px solid rgba(232,184,42,0.2)'
                                  }}>
                                    📍 {div}
                                  </td>
                                </tr>
                                {renderTableRows(divSorted, divLeader)}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // No divisions (quick league or single-division) — flat sort
                const sorted = [...confTeams].sort((a, b) => {
                  const pa = a.s.wins + a.s.losses === 0 ? 0 : a.s.wins / (a.s.wins + a.s.losses);
                  const pb = b.s.wins + b.s.losses === 0 ? 0 : b.s.wins / (b.s.wins + b.s.losses);
                  if (pb !== pa) return pb - pa;
                  return (b.s.runs_scored - b.s.runs_allowed) - (a.s.runs_scored - a.s.runs_allowed);
                });
                const leader = sorted[0];
                return (
                  <div key={conf} className="conf-standings-block" style={{width:'100%'}}>
                    <h4 className="conf-standings-header">
                      🏟️ {conf === 'AL' ? 'American League' : conf === 'NL' ? 'National League' : conf}
                    </h4>
                    <table className="standings-table standings-full">
                      <thead><tr>
                        <th>#</th><th className="team-th">Team</th>
                        <th>W</th><th>L</th><th>Pct</th><th>GB</th>
                        <th>L10</th><th>Strk</th><th>Div</th>
                        <th>Home</th><th>Away</th><th>RS</th><th>RA</th><th>Diff</th>
                      </tr></thead>
                      <tbody>{renderTableRows(sorted, leader)}</tbody>
                    </table>
                  </div>
                );
              });
            })()
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <LeagueStatsTab playerStats={playerStats} playoffPlayerStats={playoffPlayerStats} teams={teams} standings={standings} navigate={navigate} currentSeasonYear={league?.season_year || simState?.season_year || new Date().getFullYear()} leagueId={leagueId} />
      )}

      {activeTab === 'free-agents' && leagueId && (
        <div className="tab-content">
          <FreeAgentsTab leagueId={leagueId} currentUserId={currentUserId} onNavigate={onNavigate} />
        </div>
      )}

      {activeTab === 'games' && (
        <div className="tab-content">
          <h3>Schedule</h3>
          <div className="schedule-list">
            {completedGames.length > 0 ? (() => {
              let lastDay: number | null = null;
              return completedGames.map(g => {
                const home = getTeam(g.home_team_id);
                const away = getTeam(g.away_team_id);
                const homeWon = g.winner === g.home_team_id;
                const showDayHeader = g.game_day !== lastDay;
                lastDay = g.game_day;

                let dayLabel: string;
                if (g.is_playoff) {
                  const yr = league?.season_year || simState?.season_year || new Date().getFullYear();
                  const date = gameDayToDate(g.game_day, yr, 'playoffs');
                  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  dayLabel = `Playoffs G${g.game_day} — ${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
                } else {
                  const yr = league?.season_year || simState?.season_year || new Date().getFullYear();
                  const date = gameDayToDate(g.game_day, yr, 'active');
                  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  dayLabel = `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
                }

                return (
                  <div key={g.id}>
                    {showDayHeader && (
                      <div className="schedule-day-header">{dayLabel}</div>
                    )}
                    <div
                      className="schedule-game-row"
                      onClick={() => setSelectedGameId(g.game_id ?? g.id)}
                    >
                      <span className={`sg-team ${!homeWon ? 'sg-winner' : 'sg-loser'}`}>{away?.abbreviation || '???'}</span>
                      <span className={`sg-score ${!homeWon ? 'sg-winner' : ''}`}>{g.away_score}</span>
                      <span className="sg-at">@</span>
                      <span className={`sg-team ${homeWon ? 'sg-winner' : 'sg-loser'}`}>{home?.abbreviation || '???'}</span>
                      <span className={`sg-score ${homeWon ? 'sg-winner' : ''}`}>{g.home_score}</span>
                    </div>
                  </div>
                );
              });
            })() : <p>No games completed yet.</p>}
          </div>
        </div>
      )}

      {activeTab === 'playoffs' && (
        <PlayoffsTab leagueId={leagueId || ''} teams={teams} />
      )}

      {activeTab === 'draft' && (
        <DraftTab leagueId={leagueId || ''} teams={teams} currentUserId={currentUserId} onNavigate={onNavigate} />
      )}


      {/* BoxScore Modal */}
      {selectedGameId && (
        <BoxScore gameId={selectedGameId} leagueId={leagueId} onClose={() => setSelectedGameId(null)} />
      )}
    </>
    )}
    </div>
    </>
  );
};

// Inline Free Agents Tab component for league page
type FreeAgentsTabProps = { leagueId: string; currentUserId?: string | null; onNavigate?: (r: string) => void };
function FreeAgentsTab({ leagueId, currentUserId, onNavigate }: FreeAgentsTabProps) {
  const [faPlayers, setFaPlayers] = useState<any[]>([]);
  const [myTeam, setMyTeam] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [bidModal, setBidModal] = useState<any>(null);
  const [bidAmount, setBidAmount] = useState(0);
  const [waiveId, setWaiveId] = useState('');
  const [roster, setRoster] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{type:string;text:string}|null>(null);

  useEffect(() => {
    loadData();
  }, [leagueId, currentUserId]);

  const loadData = async () => {
    setLoading(true);
    // Load FA pool players
    const { data: players } = await supabase.from('players').select('*').eq('league_id', leagueId).is('team_id', null).eq('is_scrub', false).order('overall', { ascending: false });
    setFaPlayers(players || []);
    // Find user's team
    if (currentUserId) {
      const { data: teams } = await supabase.from('teams').select('*').eq('league_id', leagueId).eq('owner_id', currentUserId).limit(1);
      if (teams && teams.length > 0) {
        setMyTeam(teams[0]);
        const { data: rosterData } = await supabase.from('players').select('id, first_name, last_name, position, overall, is_scrub').eq('team_id', teams[0].id).order('is_scrub', { ascending: true });
        setRoster(rosterData || []);
      }
    }
    setLoading(false);
  };

  const submitBid = async () => {
    if (!bidModal || !myTeam) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setMsg({type:'error', text:'Not authenticated'}); setSubmitting(false); return; }
      const r = await fetch(`${FUNCTIONS_URL}/fa-bid-v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          leagueId, faPlayerId: bidModal.id,
          salaryOffer: bidAmount, waivePlayerId: waiveId,
        }),
      });
      const data = await r.json();
      if (data.error) { setMsg({type:'error', text: data.error}); }
      else { setMsg({type:'success', text: `✅ Bid submitted! Waiving ${data.waivePlayer || 'player'}`}); setBidModal(null); loadData(); }
    } catch (e: any) { setMsg({type:'error', text: e.message}); }
    setSubmitting(false);
  };

  if (loading) return <div style={{padding:40,textAlign:'center',color:'#666'}}>Loading free agents...</div>;

  return (
    <div style={{padding:16}}>
      {msg && <div style={{padding:10,marginBottom:12,borderRadius:6,textAlign:'center',background:msg.type==='error'?'#7f1d1d':'#14532d',color:'#fff',fontSize:'0.9rem'}}>{msg.text}</div>}
      {faPlayers.length === 0 ? (
        <p style={{color:'#666',textAlign:'center'}}>No free agents available.</p>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:8}}>
          {faPlayers.map(p => (
            <div key={p.id} style={{background:'#0f172a',borderRadius:8,padding:12,border:'1px solid #1e293b'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <span style={{color:'#fff',fontWeight:600}}>{p.first_name} {p.last_name}</span>
                <span style={{color:getOverallColor(p.overall),fontWeight:700}}>{numberToGrade(p.overall)}</span>
              </div>
              <div style={{color:'#6b7280',fontSize:'0.8rem'}}>{p.position} · ${((p.salary_requirement || 50000)).toLocaleString()}</div>
              {myTeam && (
                <button onClick={() => { setBidModal(p); setBidAmount(p.salary_requirement || 100000); setWaiveId(''); }}
                  style={{marginTop:8,width:'100%',padding:'6px',borderRadius:6,border:'none',background:'#22c55e',color:'#fff',cursor:'pointer',fontSize:'0.8rem',fontWeight:600}}>
                  Make Offer
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bid Modal */}
      {bidModal && myTeam && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}
          onClick={() => setBidModal(null)}>
          <div style={{background:'#0d1117',borderRadius:16,maxWidth:400,width:'100%',border:'1px solid rgba(255,255,255,0.1)',padding:24}} onClick={e => e.stopPropagation()}>
            <h3 style={{color:'#fff',margin:'0 0 16px',textAlign:'center'}}>🤝 Offer Contract</h3>
            <p style={{color:'#9ca3af',textAlign:'center',marginBottom:12}}>{bidModal.first_name} {bidModal.last_name} · {bidModal.position} · OVR {numberToGrade(bidModal.overall)}</p>
            <div style={{marginBottom:12}}>
              <label style={{color:'#aaa',fontSize:'0.85rem',display:'block',marginBottom:6}}>Salary Offer</label>
              <input type="number" value={bidAmount} onChange={e => setBidAmount(parseInt(e.target.value) || 0)}
                style={{width:'100%',padding:'10px 14px',borderRadius:8,border:'1px solid #444',background:'#1a1a2e',color:'#fff',fontSize:'1rem',boxSizing:'border-box'}} />
            </div>
            <div style={{marginBottom:12}}>
              <label style={{color:'#aaa',fontSize:'0.85rem',display:'block',marginBottom:6}}>Waive Player (sign one, waive one)</label>
              <select value={waiveId} onChange={e => setWaiveId(e.target.value)}
                style={{width:'100%',padding:'10px 14px',borderRadius:8,border:'1px solid #444',background:'#1a1a2e',color:'#fff',fontSize:'0.9rem',boxSizing:'border-box'}}>
                <option value="">Select player to waive...</option>
                {roster.map(r => (
                  <option key={r.id} value={r.id}>{r.first_name} {r.last_name} ({r.position}, OVR {numberToGrade(r.overall)}){r.is_scrub ? ' 🟡' : ''}</option>
                ))}
              </select>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setBidModal(null)}
                style={{flex:1,padding:12,borderRadius:8,border:'1px solid #444',background:'transparent',color:'#888',cursor:'pointer',fontSize:16}}>Cancel</button>
              <button onClick={submitBid} disabled={submitting || !waiveId || bidAmount <= 0}
                style={{flex:2,padding:12,borderRadius:8,border:'none',background:submitting?'#444':'#22c55e',color:'#fff',cursor:submitting?'default':'pointer',fontSize:16,fontWeight:600}}>
                {submitting ? 'Submitting...' : 'Submit Bid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// League Stats Tab Component
const LeagueStatsTab: React.FC<{ playerStats: any[]; playoffPlayerStats: any[]; teams: TeamFromDB[]; standings: Map<string, any>; navigate: (path: string) => void; currentSeasonYear: number; leagueId?: string }> = ({ playerStats, playoffPlayerStats, teams, standings, navigate, currentSeasonYear }) => {
  const [view, setView] = useState<'hitting' | 'pitching'>('hitting');
  const [statMode, setStatMode] = useState<'individual' | 'team'>('individual');
  const [sortCol, setSortCol] = useState('hr');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const perPage = 50;
  const [selectedSeason, setSelectedSeason] = useState<number | 'career' | 'current'>('current');
  const [seasonType, setSeasonType] = useState<'regular' | 'playoffs'>('regular');
  
  // Get available seasons from playerStats or playoffPlayerStats
  const activeDataSource = seasonType === 'playoffs' ? playoffPlayerStats : playerStats;
  const seasonSet = new Set(activeDataSource.map((s: any) => s.season_year).filter(Boolean));
  const availableSeasons = Array.from(seasonSet).sort((a, b) => (b as number) - (a as number));

  const teamMap = new Map(teams.map(t => [t.id, t]));
  const getTeamName = (tid: string) => { const t = teamMap.get(tid); return t ? t.name : '—'; };

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(['era', 'whip'].includes(col) ? 'asc' : 'desc'); }
    setPage(0);
  };

  const SortTh: React.FC<{ col: string; children: React.ReactNode }> = ({ col, children }) => (
    <th style={{ cursor: 'pointer' }} onClick={() => handleSort(col)}>
      {children} {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  // Filter by selected season or aggregate for career
  const filteredStats = selectedSeason === 'career'
    ? (() => {
        // Aggregate all seasons per player
        const byPlayer = new Map<string, any>();
        activeDataSource.forEach(s => {
          const existing = byPlayer.get(s.player_id);
          if (existing) {
            existing.games += s.games || 0;
            existing.at_bats += s.at_bats || 0;
            existing.hits += s.hits || 0;
            existing.runs += s.runs || 0;
            existing.doubles += s.doubles || 0;
            existing.triples += s.triples || 0;
            existing.home_runs += s.home_runs || 0;
            existing.rbi += s.rbi || 0;
            existing.walks += s.walks || 0;
            existing.strikeouts += s.strikeouts || 0;
            existing.stolen_bases += s.stolen_bases || 0;
            existing.wins += s.wins || 0;
            existing.losses += s.losses || 0;
            existing.saves += s.saves || 0;
            existing.innings_pitched = (parseFloat(existing.innings_pitched || 0) + parseFloat(s.innings_pitched || 0)).toString();
            existing.earned_runs += s.earned_runs || 0;
            existing.hits_allowed += s.hits_allowed || 0;
            existing.walks_allowed += s.walks_allowed || 0;
            existing.strikeouts_pitched += s.strikeouts_pitched || 0;
          } else {
            byPlayer.set(s.player_id, { ...s });
          }
        });
        return Array.from(byPlayer.values());
      })()
    : activeDataSource.filter(p => p.season_year === (selectedSeason === 'current' ? currentSeasonYear : selectedSeason));

  // Enrich with computed stats
  const hitters = filteredStats
    .filter(p => (p.at_bats || 0) > 0 && (p.position || '').toUpperCase() !== 'P')
    .map(p => {
      const ab = p.at_bats || 0, h = p.hits || 0, bb = p.walks || 0;
      const singles = h - (p.doubles||0) - (p.triples||0) - (p.home_runs||0);
      const tb = singles + (p.doubles||0)*2 + (p.triples||0)*3 + (p.home_runs||0)*4;
      const hbp = p.hit_by_pitch || 0;
      const sf = p.sacrifice_flies || 0;
      const avg = ab > 0 ? h/ab : 0;
      const obpDenom = ab + bb + hbp + sf;
      const obp = obpDenom > 0 ? (h + bb + hbp) / obpDenom : 0;
      const slg = ab > 0 ? tb/ab : 0;
      return { ...p, _avg: avg, _obp: obp, _slg: slg, _ops: obp+slg, _h: h, _2b: p.doubles||0, _3b: p.triples||0, _hr: p.home_runs||0, _rbi: p.rbi||0, _r: p.runs||0, _bb: bb, _k: p.strikeouts||0, _sb: p.stolen_bases||0, _ab: ab, _g: p.games||0 };
    });

  const pitchers = filteredStats
    .filter(p => parseFloat(p.innings_pitched) > 0 || (p.wins||0) > 0)
    .map(p => {
      const ip = parseFloat(p.innings_pitched) || 0;
      const era = ip > 0 ? (p.earned_runs||0)*9/ip : 99;
      const whip = ip > 0 ? ((p.walks_allowed||0)+(p.hits_allowed||0))/ip : 99;
      return { ...p, _era: era, _ip: ip, _w: p.wins||0, _l: p.losses||0, _k: p.strikeouts_pitched||0, _bb: p.walks_allowed||0, _whip: whip, _g: p.games||0, _gs: p.starts||0, _sv: p.saves||0, _er: p.earned_runs||0, _ha: p.hits_allowed||0 };
    });

  // ── Team aggregation ──
  // Aggregate hitting stats per team
  const teamHitting = (() => {
    const byTeam = new Map<string, any>();
    hitters.forEach(p => {
      const key = p.team_id || 'FA';
      if (!byTeam.has(key)) {
        byTeam.set(key, { team_id: key, team_name: getTeamName(key), _g: 0, _ab: 0, _h: 0, _2b: 0, _3b: 0, _hr: 0, _rbi: 0, _r: 0, _bb: 0, _k: 0, _sb: 0 });
      }
      const t = byTeam.get(key)!;
      t._g = Math.max(t._g, p._g);
      t._ab += p._ab; t._h += p._h; t._2b += p._2b; t._3b += p._3b;
      t._hr += p._hr; t._rbi += p._rbi; t._r += p._r;
      t._bb += p._bb; t._k += p._k; t._sb += p._sb;
    });
    return Array.from(byTeam.values())
      .filter(t => t._ab > 0)
      .map(t => ({
        ...t,
        _avg: t._ab > 0 ? t._h / t._ab : 0,
        _obp: (t._ab + t._bb) > 0 ? t._h / (t._ab + t._bb) : 0, // simplified OBP
        _slg: t._ab > 0 ? (t._h - t._2b - t._3b - t._hr + t._2b*2 + t._3b*3 + t._hr*4) / t._ab : 0,
        _ops: 0,
      }))
      .map(t => ({ ...t, _ops: t._obp + t._slg }));
  })();

  // Aggregate pitching stats per team
  const teamPitching = (() => {
    const byTeam = new Map<string, any>();
    pitchers.forEach(p => {
      const key = p.team_id || 'FA';
      if (!byTeam.has(key)) {
        const st = standings.get(key);
        byTeam.set(key, { team_id: key, team_name: getTeamName(key), _w: st ? (st.wins || 0) : 0, _l: st ? (st.losses || 0) : 0, _sv: 0, _ip: 0, _er: 0, _ha: 0, _k: 0, _bb: 0, _gs: 0 });
      }
      const t = byTeam.get(key)!;
      t._sv += p._sv;
      t._ip += p._ip; t._er += p._er; t._ha += p._ha;
      t._k += p._k; t._bb += p._bb;
      t._gs += p._gs;  // sum of starts = total team games
    });
    return Array.from(byTeam.values())
      .filter(t => t._ip > 0 || t._w > 0)
      .map(t => ({
        ...t,
        _g: t._gs,  // team G = sum of starts (one starter per game)
        _era: t._ip > 0 ? (t._er * 9) / t._ip : 99,
        _whip: t._ip > 0 ? (t._ha + t._bb) / t._ip : 99,
      }));
  })();

  const data = statMode === 'team'
    ? (view === 'hitting' ? teamHitting : teamPitching)
    : (view === 'hitting' ? hitters : pitchers);
  const sorted = [...data].sort((a: any, b: any) => {
    const av = a['_' + sortCol] ?? a[sortCol] ?? 0;
    const bv = b['_' + sortCol] ?? b[sortCol] ?? 0;
    return sortDir === 'asc' ? av - bv : bv - av;
  });
  const paged = sorted.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(sorted.length / perPage);

  if (activeDataSource.length === 0) return (
    <div className="tab-content">
      <div style={{display:'flex',gap:10,marginBottom:15}}>
        <button className="tab active" onClick={()=>{}}>Regular Season</button>
        <button className="tab" onClick={()=>setSeasonType('playoffs')}>🏆 Playoffs</button>
      </div>
      <p>No {seasonType === 'playoffs' ? 'playoff' : ''} stats yet.</p>
    </div>
  );

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', gap: 15, marginBottom: 15, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className={`tab ${seasonType === 'regular' ? 'active' : ''}`}
            onClick={() => { setSeasonType('regular'); setSortCol('hr'); setSortDir('desc'); setPage(0); setSelectedSeason('current'); }}
          >📅 Regular Season</button>
          <button
            className={`tab ${seasonType === 'playoffs' ? 'active' : ''}`}
            onClick={() => { setSeasonType('playoffs'); setSortCol('hr'); setSortDir('desc'); setPage(0); setSelectedSeason('current'); }}
          >🏆 Playoffs</button>
        </div>
        <div style={{ display: 'flex', gap: 10, borderLeft: '1px solid #334155', paddingLeft: 12 }}>
          <button className={`tab ${view === 'hitting' ? 'active' : ''}`} onClick={() => { setView('hitting'); setSortCol('hr'); setSortDir('desc'); setPage(0); }}>🏏 Hitting</button>
          <button className={`tab ${view === 'pitching' ? 'active' : ''}`} onClick={() => { setView('pitching'); setSortCol('w'); setSortDir('desc'); setPage(0); }}>⚾ Pitching</button>
        </div>
        <div style={{ display: 'flex', gap: 8, borderLeft: '1px solid #334155', paddingLeft: 12 }}>
          <button
            className={`tab ${statMode === 'individual' ? 'active' : ''}`}
            onClick={() => { setStatMode('individual'); setSortCol(view === 'hitting' ? 'hr' : 'w'); setSortDir('desc'); setPage(0); }}
            style={{ fontSize: 13 }}
          >
            👤 Players
          </button>
          <button
            className={`tab ${statMode === 'team' ? 'active' : ''}`}
            onClick={() => { setStatMode('team'); setSortCol(view === 'hitting' ? 'hr' : 'w'); setSortDir('desc'); setPage(0); }}
            style={{ fontSize: 13 }}
          >
            👥 Teams
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="season-select" style={{ color: '#9ca3af', fontSize: 14 }}>Season:</label>
          <select
            id="season-select"
            value={selectedSeason === 'career' ? 'career' : selectedSeason === 'current' ? 'current' : selectedSeason}
            onChange={e => {
              const val = e.target.value;
              setSelectedSeason(val === 'career' ? 'career' : val === 'current' ? 'current' : parseInt(val));
              setPage(0);
            }}
            style={{
              background: '#1e293b',
              color: '#fff',
              border: '1px solid #334155',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            <option value="current">Current Season ({currentSeasonYear})</option>
            <option value="career">Career</option>
            {availableSeasons.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        {view === 'hitting' ? (
          <table className="standings-table">
            <thead><tr>
              <th>#</th>
              {statMode === 'individual' ? <th>Player</th> : null}
              <th>{statMode === 'team' ? 'Team' : null}Team</th>
              {statMode === 'individual' ? <th>Pos</th> : null}
              <SortTh col="g">G</SortTh><SortTh col="ab">AB</SortTh><SortTh col="h">H</SortTh>
              <SortTh col="2b">2B</SortTh><SortTh col="3b">3B</SortTh>
              <SortTh col="hr">HR</SortTh><SortTh col="rbi">RBI</SortTh><SortTh col="r">R</SortTh>
              <SortTh col="bb">BB</SortTh><SortTh col="k">K</SortTh><SortTh col="sb">SB</SortTh>
              <SortTh col="avg">AVG</SortTh><SortTh col="obp">OBP</SortTh><SortTh col="slg">SLG</SortTh><SortTh col="ops">OPS</SortTh>
            </tr></thead>
            <tbody>
              {paged.map((p: any, i: number) => (
                <tr
                  key={statMode === 'team' ? p.team_id : p.player_id}
                  style={statMode === 'individual' ? { cursor: 'pointer' } : {}}
                  onClick={() => statMode === 'individual' ? navigate(`/player/${p.player_id}`) : undefined}
                >
                  <td>{page * perPage + i + 1}</td>
                  {statMode === 'individual' ? (
                    <td style={{ color: '#60a5fa' }}>
                      {p.playerName || 'Unknown'}
                    </td>
                  ) : null}
                  <td style={statMode === 'team' ? { fontWeight: 600, color: '#60a5fa' } : {}}>
                    {statMode === 'team' ? (
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/team/${p.team_id}`); }}
                        onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                        onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                      >
                        {p.team_name}
                      </span>
                    ) : getTeamName(p.team_id)}
                  </td>
                  {statMode === 'individual' ? <td>{p.position || '-'}</td> : null}
                  <td>{p._g}</td><td>{p._ab}</td><td>{p._h}</td>
                  <td>{p._2b}</td><td>{p._3b}</td>
                  <td>{p._hr}</td><td>{p._rbi}</td><td>{p._r}</td>
                  <td>{p._bb}</td><td>{p._k}</td><td>{p._sb}</td>
                  <td>{p._avg.toFixed(3).replace(/^0/,'')}</td>
                  <td>{p._obp?.toFixed ? p._obp.toFixed(3).replace(/^0/,'') : '.000'}</td>
                  <td>{p._slg?.toFixed ? p._slg.toFixed(3).replace(/^0/,'') : '.000'}</td>
                  <td>{p._ops?.toFixed ? p._ops.toFixed(3).replace(/^0/,'') : '.000'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="standings-table">
            <thead><tr>
              <th>#</th>
              {statMode === 'individual' ? <th>Player</th> : null}
              <th>{statMode === 'team' ? 'Team' : 'Team'}</th>
              <SortTh col="g">G</SortTh><SortTh col="gs">GS</SortTh><SortTh col="w">W</SortTh><SortTh col="l">L</SortTh>
              <SortTh col="sv">SV</SortTh><SortTh col="era">ERA</SortTh><SortTh col="ip">IP</SortTh>
              <SortTh col="k">K</SortTh><SortTh col="bb">BB</SortTh><SortTh col="whip">WHIP</SortTh>
            </tr></thead>
            <tbody>
              {paged.map((p: any, i: number) => (
                <tr
                  key={statMode === 'team' ? p.team_id : p.player_id}
                  style={statMode === 'individual' ? { cursor: 'pointer' } : {}}
                  onClick={() => statMode === 'individual' ? navigate(`/player/${p.player_id}`) : undefined}
                >
                  <td>{page * perPage + i + 1}</td>
                  {statMode === 'individual' ? (
                    <td style={{ color: '#60a5fa' }}>
                      {p.playerName || 'Unknown'}
                    </td>
                  ) : null}
                  <td style={statMode === 'team' ? { fontWeight: 600, color: '#60a5fa' } : {}}>
                    {statMode === 'team' ? (
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/team/${p.team_id}`); }}
                        onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                        onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                      >
                        {p.team_name}
                      </span>
                    ) : getTeamName(p.team_id)}
                  </td>
                  <td>{p._g}</td><td>{p._gs || 0}</td><td>{p._w}</td><td>{p._l}</td>
                  <td>{p._sv}</td>
                  <td>{p._era < 50 ? p._era.toFixed(2) : '-.--'}</td>
                  <td>{p._ip.toFixed(1)}</td>
                  <td>{p._k}</td><td>{p._bb}</td>
                  <td>{p._whip < 10 ? p._whip.toFixed(2) : '-.--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 15 }}>
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '5px 15px' }}>← Prev</button>
          <span style={{ padding: '5px 10px' }}>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '5px 15px' }}>Next →</button>
        </div>
      )}
    </div>
  );
};

// Playoffs Tab Component — uses playoff_brackets/series/games tables
interface PlayoffSeriesDto {
  id: string;
  round: string;
  conference: string;
  series_name: string;
  home_team_id: string | null;
  away_team_id: string | null;
  home_wins: number;
  away_wins: number;
  winner: string | null;
  status: string;
  start_day: number;
}

interface PlayoffGameDto {
  id: string;
  series_id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number | null;
  away_score: number | null;
  winner: string | null;
  game_day: number;
  game_number: number;
  completed: boolean;
}

const PlayoffsTab: React.FC<{ leagueId: string; teams: TeamFromDB[] }> = ({ leagueId, teams }) => {
  const [seriesList, setSeriesList] = useState<PlayoffSeriesDto[]>([]);
  const [games, setGames] = useState<PlayoffGameDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState<any>(null);

  useEffect(() => {
    loadPlayoffs();
  }, [leagueId]);

  async function loadPlayoffs() {
    if (!isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return;
    }
    
    // Load league for season_year + champion info
    const { data: leagueData } = await supabase
      .from('leagues')
      .select('*')
      .eq('id', leagueId)
      .single();
    if (leagueData) setLeague(leagueData);

    const seasonYear = leagueData?.season_year || 1902;

    // Find bracket
    const { data: bracketData } = await supabase
      .from('playoff_brackets')
      .select('id')
      .eq('league_id', leagueId)
      .eq('season_year', seasonYear)
      .limit(1);
    
    if (!bracketData || bracketData.length === 0) {
      // No bracket — try direct playoff games from league_schedule (Quick League path)
      const { data: qlGames } = await supabase
        .from('league_schedule')
        .select('id, game_id, series_id, home_team_id, away_team_id, home_score, away_score, winner, game_day, game_number, status')
        .eq('league_id', leagueId)
        .eq('is_playoff', true)
        .order('game_day')
        .order('game_number');
      
      if (qlGames && qlGames.length > 0) {
        setGames((qlGames as any[]).map(g => ({
          id: g.id, series_id: 'ql-direct',
          home_team_id: g.home_team_id, away_team_id: g.away_team_id,
          home_score: g.home_score, away_score: g.away_score,
          winner: g.winner, game_day: g.game_day, game_number: g.game_number,
          completed: g.status === 'completed',
        })));
        setSeriesList([{ id: 'ql-direct', bracket_id: '', round: 'QL', conference: '', series_name: 'Quick League Playoffs', home_team_id: '', away_team_id: '', home_wins: 0, away_wins: 0, status: 'active', start_day: 0 } as any]);
      }
      setLoading(false);
      return;
    }

    const bracketId = bracketData[0].id;

    // Load series
    const { data: seriesData } = await supabase
      .from('playoff_series')
      .select('*')
      .eq('bracket_id', bracketId);
    
    if (seriesData) setSeriesList(seriesData as PlayoffSeriesDto[]);

    // Load games from league_schedule (engine writes here, not playoff_games)
    const { data: gameData } = await supabase
      .from('league_schedule')
      .select('id, game_id, series_id, home_team_id, away_team_id, home_score, away_score, winner, game_day, game_number, status')
      .eq('league_id', leagueId)
      .eq('is_playoff', true)
      .order('game_day')
      .order('game_number');

    const normalized = (gameData || []).map((g: any) => ({
      id: g.id,
      series_id: g.series_id,
      home_team_id: g.home_team_id,
      away_team_id: g.away_team_id,
      home_score: g.home_score,
      away_score: g.away_score,
      winner: g.winner,
      game_day: g.game_day,
      game_number: g.game_number,
      completed: g.status === 'completed',
    }));
    setGames(normalized as PlayoffGameDto[]);
    setLoading(false);
  }

  if (loading) return <div className="tab-content"><p>Loading playoffs...</p></div>;
  if (seriesList.length === 0) return <div className="tab-content"><p>No playoff bracket yet. Playoffs begin after the regular season ends.</p></div>;

  // Build standings — use playoff_series win counts directly (authoritative)
  // For active series, recount from completed games for live accuracy
  const standings: Record<string, { t1: string; t2: string; t1w: number; t2w: number; round: string; name: string }> = {};
  for (const s of seriesList) {
    let t1w = s.home_wins || 0;
    let t2w = s.away_wins || 0;
    // Recount from games if series is still active (live accuracy)
    if (s.status === 'active') {
      t1w = 0; t2w = 0;
      for (const g of games) {
        if (g.series_id !== s.id || !g.completed || !g.winner) continue;
        if (g.winner === (s.home_team_id || '')) t1w++;
        else if (g.winner === (s.away_team_id || '')) t2w++;
      }
    }
    standings[s.id] = {
      t1: s.home_team_id || '',
      t2: s.away_team_id || '',
      t1w, t2w,
      round: s.round,
      name: s.series_name || s.id
    };
  }

  // Group by round
  const byRound: Record<string, PlayoffSeriesDto[]> = { 'DS': [], 'CS': [], 'WS': [], 'QL': [], 'SEMI': [], 'CHAMP': [] };
  for (const s of seriesList) {
    if (byRound[s.round]) byRound[s.round].push(s);
  }

  const roundOrder: Record<string, number> = { 'DS': 1, 'CS': 2, 'WS': 3, 'SEMI': 0, 'CHAMP': 1, 'QL': 0 };
  const roundLabels: Record<string, string> = { 'DS': 'Division Series', 'CS': 'Conference Series', 'WS': 'World Series', 'SEMI': 'Semi-Finals', 'CHAMP': 'Championship', 'QL': 'Quick League Playoffs' };

  const teamMap = new Map(teams.map(t => [t.id, t]));
  const getTeamName = (id: string) => teamMap.get(id)?.name || 'TBD';
  const getTeamAbbr = (id: string) => teamMap.get(id)?.abbreviation || '??';

  // Find WS champion
  const wsStanding = standings[seriesList.find(s => s.round === 'WS')?.id || ''];
  const wsChampion = wsStanding && (wsStanding.t1w >= 4 || wsStanding.t2w >= 4)
    ? getTeamName(wsStanding.t1w >= 4 ? wsStanding.t1 : wsStanding.t2)
    : null;

  return (
    <div className="tab-content">
      <h3>⚾ Playoffs</h3>
      
      {(league?.status === 'off_season' || wsChampion) && (
        <div style={{
          textAlign: 'center', padding: '16px', marginBottom: '20px',
          background: 'linear-gradient(135deg, rgba(212,160,23,0.15), rgba(232,184,42,0.05))',
          border: '1px solid #d4a017', borderRadius: '12px'
        }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#d4a017', textTransform: 'uppercase' }}>
            {league?.status === 'off_season' ? 'Season Complete' : 'Playoffs'}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#e8b82a', marginTop: '4px' }}>
            🏆 {league?.champion_name || wsChampion || '—'}
          </div>
          <div style={{ fontSize: '13px', color: '#889', marginTop: '4px' }}>
            World Series Champion — Season {league?.season_year || 1902}
          </div>
        </div>
      )}
      
      {Object.entries(byRound)
        .filter(([_, list]) => list.length > 0)
        .sort((a, b) => roundOrder[a[0]] - roundOrder[b[0]])
        .map(([round, list]) => (
          <div key={round} style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#3584e4', borderBottom: '1px solid #334', paddingBottom: '8px' }}>
              {roundLabels[round as keyof typeof roundLabels] || round}
            </h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {list.map(series => {
                const st = standings[series.id];
                const t1Name = getTeamName(st?.t1 || '');
                const t2Name = getTeamName(st?.t2 || '');
                const t1w = st?.t1w || 0;
                const t2w = st?.t2w || 0;
                const isComplete = t1w >= 4 || t2w >= 4;
                const winner = t1w >= 4 ? t1Name : t2w >= 4 ? t2Name : null;

                return (
                  <div key={series.id} style={{
                    background: isComplete ? 'rgba(42, 157, 143, 0.1)' : 'rgba(30, 40, 55, 0.8)',
                    border: `1px solid ${isComplete ? '#2a9d8f' : '#334'}`,
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12px', color: '#678', textTransform: 'uppercase' }}>
                        {series.series_name || series.id.replace(/_/g, ' ')}
                      </span>
                      {isComplete && (
                        <span style={{ fontSize: '12px', color: '#2a9d8f', fontWeight: 600 }}>
                          ✓ {winner}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginBottom: '2px' }}>
                        <span style={{ color: t1w >= 4 ? '#fff' : '#aaa' }}>{t1Name}</span>
                        <span style={{ color: '#3584e4', fontWeight: 700, fontSize: '18px' }}>{t1w}</span>
                      </div>
                      <div style={{ textAlign: 'center', color: '#567', fontSize: '10px', padding: '2px' }}>vs</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>
                        <span style={{ color: t2w >= 4 ? '#fff' : '#aaa' }}>{t2Name}</span>
                        <span style={{ color: '#3584e4', fontWeight: 700, fontSize: '18px' }}>{t2w}</span>
                      </div>
                    </div>

                    {/* Game pills */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {games.filter(g => g.series_id === series.id && g.completed).sort((a, b) => a.game_number - b.game_number).map(g => (
                        <div key={g.id} style={{
                          fontSize: '11px', padding: '3px 6px',
                          background: 'rgba(0,0,0,0.3)', borderRadius: '4px',
                          color: g.winner === st?.t1 ? '#2a9d8f' : '#e9c46a'
                        }}>
                          G{g.game_number}: {getTeamAbbr(g.winner || '')} W
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  );
};

// Draft Tab — shows draft info for traditional leagues
type DraftTabProps = { leagueId: string; teams: any[]; currentUserId?: string | null; onNavigate?: (r: string) => void };
function DraftTab({ leagueId, teams, currentUserId, onNavigate }: DraftTabProps) {
  const [draftPicks, setDraftPicks] = useState<any[]>([]);
  const [retirees, setRetirees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRound, setFilterRound] = useState(0);

  useEffect(() => { loadData(); }, [leagueId]);

  const loadData = async () => {
    setLoading(true);
    const { data: picks } = await supabase
      .from('draft_picks')
      .select('id, round, pick_number, team_id, player_id, status, draft_position, players(first_name, last_name, position, overall), teams(name)')
      .eq('league_id', leagueId)
      .order('pick_number', { ascending: true });
    setDraftPicks(picks || []);

    const { data: teamIds } = await supabase
      .from('teams')
      .select('id')
      .eq('league_id', leagueId);
    if (teamIds && teamIds.length > 0) {
      const ids = teamIds.map((t: any) => t.id);
      const { data: ret } = await supabase
        .from('players')
        .select('id, first_name, last_name, position, overall, career_season, team_id')
        .in('team_id', ids)
        .gte('career_season', 9)
        .eq('retired', false)
        .order('overall', { ascending: false });
      setRetirees(ret || []);
    }
    setLoading(false);
  };

  const getTeamAbbr = (teamId: string) => {
    const t = teams.find((t: any) => t.id === teamId);
    return t?.abbreviation || '???';
  };

  const rounds = [...new Set(draftPicks.map(p => p.round))].sort();
  const filteredPicks = filterRound ? draftPicks.filter(p => p.round === filterRound) : draftPicks;
  const availablePicks = draftPicks.filter(p => p.status === 'available');
  const pickedPicks = draftPicks.filter(p => p.status === 'picked');

  if (loading) return <div style={{padding:40,textAlign:'center',color:'#666'}}>Loading draft info...</div>;

  return (
    <div className="tab-content" style={{padding:16}}>
      <div style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',borderRadius:8,padding:20,marginBottom:16,border:'1px solid #374151'}}>
        <h3 style={{color:'#d4a017',marginBottom:12,fontSize:'1.1rem'}}>📋 Season-End Draft</h3>
        <div style={{display:'flex',gap:24,flexWrap:'wrap'}}>
          <div><div style={{color:'#9ca3af',fontSize:'0.8rem'}}>Draft Picks</div><div style={{color:'#fff',fontSize:'1.4rem',fontWeight:700}}>{draftPicks.length}</div></div>
          <div><div style={{color:'#9ca3af',fontSize:'0.8rem'}}>Available</div><div style={{color:'#22c55e',fontSize:'1.4rem',fontWeight:700}}>{availablePicks.length}</div></div>
          <div><div style={{color:'#9ca3af',fontSize:'0.8rem'}}>Selected</div><div style={{color:'#3b82f6',fontSize:'1.4rem',fontWeight:700}}>{pickedPicks.length}</div></div>
          <div><div style={{color:'#9ca3af',fontSize:'0.8rem'}}>Pending Retirees</div><div style={{color:'#f59e0b',fontSize:'1.4rem',fontWeight:700}}>{retirees.length}</div></div>
          {rounds.length > 0 && <div><div style={{color:'#9ca3af',fontSize:'0.8rem'}}>Rounds</div><div style={{color:'#fff',fontSize:'1.4rem',fontWeight:700}}>{rounds.length}</div></div>}
        </div>
        {retirees.length > 0 && (
          <div style={{marginTop:12,padding:'8px 12px',background:'rgba(245,158,11,0.1)',borderRadius:6,borderLeft:'3px solid #f59e0b'}}>
            <span style={{color:'#f59e0b',fontSize:'0.85rem'}}>⚠️ {retirees.length} player{retirees.length !== 1 ? 's' : ''} in final season — will retire and open roster spots for the draft</span>
          </div>
        )}
      </div>

      {draftPicks.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px 20px',background:'linear-gradient(135deg,#0f172a,#1e293b)',borderRadius:8,border:'1px solid #374151'}}>
          <div style={{fontSize:'2.5rem',marginBottom:12}}>📋</div>
          <h3 style={{color:'#fff',marginBottom:8}}>Draft Not Yet Generated</h3>
          <p style={{color:'#9ca3af',maxWidth:400,margin:'0 auto',lineHeight:1.6}}>
            The draft pool and pick order are generated during the off-season after retirements. Worst records pick first — straight draft (not snake).
          </p>
          {retirees.length > 0 && (
            <div style={{marginTop:16}}>
              <div style={{color:'#d4a017',fontSize:'0.9rem',marginBottom:8}}>Projected Retirees ({retirees.length})</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6,justifyContent:'center'}}>
                {retirees.slice(0,12).map(r => (
                  <span key={r.id} style={{padding:'4px 8px',background:'rgba(245,158,11,0.1)',borderRadius:4,fontSize:'0.8rem',color:'#f59e0b'}}>
                    {r.first_name} {r.last_name} ({r.position}) OVR {numberToGrade(r.overall)}
                  </span>
                ))}
                {retirees.length > 12 && <span style={{color:'#666',fontSize:'0.8rem',padding:'4px 8px'}}>+{retirees.length - 12} more...</span>}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div style={{display:'flex',gap:4,marginBottom:12,flexWrap:'wrap'}}>
            <button onClick={() => setFilterRound(0)} style={{padding:'4px 10px',borderRadius:4,cursor:'pointer',fontSize:'0.8rem',border:`1px solid ${filterRound===0?'#d4a017':'#374151'}`,background:filterRound===0?'rgba(212,160,23,0.15)':'transparent',color:filterRound===0?'#d4a017':'#9ca3af'}}>All</button>
            {rounds.map(r => (
              <button key={r} onClick={() => setFilterRound(r)} style={{padding:'4px 10px',borderRadius:4,cursor:'pointer',fontSize:'0.8rem',border:`1px solid ${filterRound===r?'#d4a017':'#374151'}`,background:filterRound===r?'rgba(212,160,23,0.15)':'transparent',color:filterRound===r?'#d4a017':'#9ca3af'}}>Round {r}</button>
            ))}
          </div>
          <div style={{overflowX:'auto'}}>
            <table className="sf-table" style={{width:'100%',fontSize:'0.85rem'}}>
              <thead>
                <tr>
                  <th style={{textAlign:'center',width:60}}>Pick</th>
                  <th style={{textAlign:'center',width:60}}>Round</th>
                  <th>Team</th>
                  <th>Player</th>
                  <th style={{textAlign:'center'}}>Pos</th>
                  <th style={{textAlign:'center'}}>OVR</th>
                  <th style={{textAlign:'center'}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPicks.map(pick => {
                  const player = pick.players || {};
                  const isPicked = pick.status === 'picked';
                  const isAvailable = pick.status === 'available';
                  return (
                    <tr key={pick.id} style={{background:isPicked?'rgba(34,197,94,0.05)':'transparent',opacity:isAvailable?1:0.7}}>
                      <td style={{textAlign:'center',color:'#d4a017',fontWeight:600}}>#{pick.pick_number}</td>
                      <td style={{textAlign:'center',color:'#9ca3af'}}>{pick.round}</td>
                      <td><span style={{color:'#fff',fontWeight:500}}>{pick.teams?.name || '???'}</span></td>
                      <td style={{color:isPicked?'#22c55e':'#fff'}}>{player.first_name||'—'} {player.last_name||'—'}</td>
                      <td style={{textAlign:'center',color:'#9ca3af'}}>{player.position||'—'}</td>
                      <td style={{textAlign:'center',color:(player.overall||0)>=65?'#f59e0b':'#9ca3af',fontWeight:600}}>{player.overall ? numberToGrade(player.overall) : '—'}</td>
                      <td style={{textAlign:'center'}}>
                        {isPicked ? <span style={{color:'#22c55e',fontSize:'0.8rem'}}>✅ Selected</span> :
                         isAvailable ? <span style={{color:'#f59e0b',fontSize:'0.8rem'}}>⏳ Available</span> :
                         <span style={{color:'#666',fontSize:'0.8rem'}}>{pick.status}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {retirees.length > 0 && draftPicks.length > 0 && (
        <div style={{marginTop:20}}>
          <h4 style={{color:'#f59e0b',marginBottom:8,fontSize:'0.95rem'}}>⚠️ Pending Retirees ({retirees.length})</h4>
          <div style={{overflowX:'auto'}}>
            <table className="sf-table" style={{width:'100%',fontSize:'0.85rem'}}>
              <thead><tr><th>Player</th><th style={{textAlign:'center'}}>Pos</th><th style={{textAlign:'center'}}>OVR</th><th style={{textAlign:'center'}}>Season</th><th>Team</th></tr></thead>
              <tbody>
                {retirees.map(r => (
                  <tr key={r.id}>
                    <td style={{color:'#fff'}}>{r.first_name} {r.last_name}</td>
                    <td style={{textAlign:'center',color:'#9ca3af'}}>{r.position}</td>
                    <td style={{textAlign:'center',color:'#f59e0b',fontWeight:600}}>{numberToGrade(r.overall)}</td>
                    <td style={{textAlign:'center',color:'#ef4444'}}>{r.career_season}/9</td>
                    <td style={{color:'#9ca3af'}}>{getTeamAbbr(r.team_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeagueDetailPage;
